/**
 * Límites y helpers de fotos para UF (user_lot_images), contenido de lote
 * (user_entity_images) y parcelas CAD (custom_parcel_images).
 * Portada = is_cover en DB; orden is_cover DESC, created_at ASC.
 *
 * Fotos opcionales (mínimo 0): se puede guardar y quedar sin fotos.
 */

export const LISTING_MAX_IMAGES = 12;
/** Mínimo de fotos en contenido de lote (`user_entity_images` / upsert). */
export const LISTING_MIN_IMAGES = 0;
/** Mínimo de fotos en UF (`user_lot_images`). */
export const LISTING_MIN_IMAGES_UNIT = 0;

export const LISTING_IMAGE_LIMIT_TOAST = {
  title: 'Límite alcanzado',
  message: `Máximo ${LISTING_MAX_IMAGES} fotos.`,
};

export const LISTING_IMAGE_MIN_TOAST = {
  title: 'Foto requerida',
  message: 'Debe quedar al menos 1 foto.',
};

export const LISTING_UPSERT_MIN_PHOTOS_TOAST = {
  title: 'Foto requerida',
  message: 'Subí al menos 1 foto antes de guardar.',
};

export const LISTING_COVER_HINT = 'Tocá una foto para usarla como portada.';

const firstImageUrl = (images) => {
  const list = Array.isArray(images) ? images : [];
  const cover = list.find((image) => image?.is_cover === true && image?.url);
  if (cover?.url) return String(cover.url);
  const found = list.find((image) => {
    const url = image?.url;
    return url != null && String(url).trim() !== '';
  });
  return found?.url ? String(found.url) : null;
};

/** Orden cronológico de carga (sin priorizar portada). */
export const sortImagesByCreatedAt = (images) => {
  const list = Array.isArray(images) ? [...images] : [];
  return list.sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (ta !== tb) return ta - tb;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  });
};

/** Orden de galería: portada primero, luego created_at ASC. */
export const sortListingImages = (images) => {
  const list = Array.isArray(images) ? [...images] : [];
  return list.sort((a, b) => {
    const coverDiff = Number(Boolean(b?.is_cover)) - Number(Boolean(a?.is_cover));
    if (coverDiff !== 0) return coverDiff;
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (ta !== tb) return ta - tb;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  });
};

const resolveCoverImageId = (images, coverImage = null) => {
  if (coverImage?.id != null) return String(coverImage.id);
  const flagged = (Array.isArray(images) ? images : []).find((img) => img?.is_cover === true);
  if (flagged?.id != null) return String(flagged.id);
  return null;
};

/**
 * Normaliza flags is_cover y orden de galería.
 * Confía en is_cover / cover_image del API; fallback legacy = más antigua por created_at.
 */
export const normalizeListingImages = (images, coverImage = null) => {
  const list = Array.isArray(images) ? images.map((image) => ({ ...image })) : [];
  if (list.length === 0) return [];

  let coverId = resolveCoverImageId(list, coverImage);

  if (!coverId) {
    const oldest = sortImagesByCreatedAt(list)[0];
    if (oldest?.id != null) coverId = String(oldest.id);
  }

  const withCover = list.map((image) => ({
    ...image,
    is_cover: coverId != null && String(image.id) === coverId,
  }));

  return sortListingImages(withCover);
};

/** Sincroniza is_cover tras set/delete cover cuando el API devuelve cover_image. */
export const applyCoverImageToListingImages = (images, coverImage) => (
  normalizeListingImages(images, coverImage ?? null)
);

export const extractImagesFromApiPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.images)) return payload.images;
  if (Array.isArray(payload?.photos)) return payload.photos;
  return [];
};

export const normalizeListingImagesFromPayload = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : { images: payload };
  return normalizeListingImages(
    extractImagesFromApiPayload(source),
    source?.cover_image ?? null,
  );
};

export const resolveCoverImageUrl = (entity) => {
  if (!entity) return null;
  const fromCover = entity?.cover_image?.url;
  if (fromCover != null && String(fromCover).trim() !== '') {
    return String(fromCover);
  }
  const list = Array.isArray(entity?.images)
    ? entity.images
    : (Array.isArray(entity?.photos) ? entity.photos : []);
  return firstImageUrl(normalizeListingImages(list, entity?.cover_image ?? null));
};

/**
 * Portada para cards de lista compartida. UF y lote no se mezclan:
 * unidad → units[].cover_image; lote sin UF → lots[].cover_image o lot_user_content.
 */
export const resolveListingCoverImage = (lot, unit) => {
  if (unit) return resolveCoverImageUrl(unit);
  return resolveCoverImageUrl(lot) || resolveCoverImageUrl(lot?.lot_user_content);
};

export const isListingImageLimitError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('12')
    || text.includes('límite')
    || text.includes('limite')
    || text.includes('limit')
  );
};

export const isListingImageMinError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('al menos 1')
    || text.includes('última')
    || text.includes('ultima')
    || text.includes('minimo')
    || text.includes('mínimo')
  );
};

export const resolveImageApiMessage = (res) => (
  res?.message || res?.error || res?.detail || ''
);
